class QuickView extends HTMLElement {
  constructor() {
    super();
    this.handle = this.dataset.handle;
    this.sectionId = this.dataset.sectionId || "quick-view"; // Default to 'quick-view' if not provided, valid values are 'quick-view' and 'quick-view-free'
    this.propertyType = this.dataset.type || "";
    this.selectedOptions = new Map();
    this.mergedProductOptions = new Set();
    this.addToCartEnter = false;
    this.productGallery = this.querySelector("[data-js-product-gallery]");
    this.isMergedProduct = false;
  }

  connectedCallback() {
    this.generateQuickView();
  }

  async generateQuickView() {
    if (
      this.getAttribute("data-handle") === this.handle &&
      this.getAttribute("data-section-id") === this.sectionId
    )
      return;
    if (!this.handle) return;
    this.startLoading();
    try {
      const response = await fetch(
        `/products/${this.handle}?section_id=${this.sectionId}`
      );
      if (!response.ok) throw new Error("Network response was not ok");

      this.innerHTML = "";
      this.insertAdjacentHTML("beforeend", await response.text());
      this.initializeQuickView();
    } catch (error) {
      console.error("Fetch error:", error);
      this.innerHTML = `<div>Please make sure you're connected to the internet and try again! :)</div>`;
    } finally {
      this.stopLoading();
      if (this.isMergedProduct) {
        this.applySelectedOptions();
        this.isMergedProduct = false;
      }
      this.setupLightbox();
      this.setupSizeChart();
      this.setupCustomGuide();
      this.setDefaultColor();
    }
  }

  setDefaultColor() {
    if (
      this.handle === "anytime-tees" &&
      this.dataset.sectionId === "quick-view-free"
    ) {
      this.querySelector(
        'input[type="radio"][value="Charcoal Gray"]'
      ).checked = true;
      this.applySelectedOptions();
    }

    return false;
  }

  initializeQuickView() {
    this.setAttribute("data-type", this.propertyType);
    this.setAttribute("data-handle", this.handle);
    this.setAttribute("data-section-id", this.sectionId);
    this.clonePropertyInput();
    this.cloneVariantInput();
    this.setupOptionHandlers();
    this.setupMergedProduct();
    this.setupCartBehavior();
    this.resetSlider();
  }

  setupLightbox() {
    let url = document.querySelector(
      'link[href*="component-lightbox.js"]'
    ).href;
    let location = document.body;
    var scriptTag = document.createElement("script");
    scriptTag.src = url;

    location.appendChild(scriptTag);
  }

  setupSizeChart() {
    const sizeChartLink = this.querySelector(".js-toggle-size-chart-modal");
    const sizeChartModalContent = this.querySelector(
      "#size-chart-modal .size-chart-modal"
    );

    if (sizeChartLink && sizeChartModalContent) {
      sizeChartLink.addEventListener("click", (event) => {
        event.preventDefault();
        const instance = basicLightbox.create(sizeChartModalContent.outerHTML, {
          onShow: (instance) => {
            instance
              .element()
              .querySelector(".basicLightboxClose")
              .addEventListener("click", () => {
                instance.close();
              });
          },
        });
        instance.show();
      });
    }
  }

  setupCustomGuide() {
    if (document.getElementById("fit-quick-view-modal")) {
      window.basicLightbox.create(
        document.getElementById("fit-quick-view-modal"),
        { trigger: document.querySelector(".js-toggle-fit-quick-view-modal") }
      );
    }
  }

  setupOptionHandlers() {
    this.querySelectorAll(".product-variant__item--radio input").forEach(
      (input) => {
        if (!this.mergedProductOptions.has(input.name)) {
          input.addEventListener("change", () =>
            this.handleOptionChange(input)
          );
        }
      }
    );
  }

  resetSlider() {
    this.productGallery = this.querySelector("[data-js-product-gallery]");
    this.productSlider = this.querySelector("css-slider");
    if (this.productSlider) {
      this.productSlider.resetSlider();
    }
  }

  handleOptionChange(input) {
    this.selectedOptions.set(input.name, input.value);
    this.updateOptionLabel(input);
    this.triggerVariantChange();
  }

  setupMergedProduct() {
    this.querySelectorAll("[data-js-merged-product]").forEach((product) => {
      const optionName = product
        .closest(".product-variant")
        .querySelector("input").name;
      this.mergedProductOptions.add(optionName);

      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handle = product.dataset.handle;
        this.isMergedProduct = true;
        this.generateQuickView();
      };

      product.addEventListener("click", handleClick);
      [product.closest("a"), product.closest("button")]
        .filter(Boolean)
        .forEach((el) =>
          el.addEventListener("click", (e) => e.preventDefault())
        );
    });
  }

  setupCartBehavior() {
    if (
      document.body.classList.contains("template-cart") ||
      KROWN.settings.cart_action !== "overlay"
    )
      return;

    const addToCartButton = this.querySelector("[data-js-product-add-to-cart]");
    const productForm = this.querySelector("[data-js-product-form]");

    addToCartButton?.addEventListener(
      "click",
      () => (this.addToCartEnter = true)
    );
    productForm?.addEventListener("add-to-cart", () => this.handleAddToCart());
  }

  applySelectedOptions() {
    this.selectedOptions.forEach((value, name) => {
      if (!this.mergedProductOptions.has(name)) {
        const input = this.querySelector(
          `input[name="${CSS.escape(name)}"][value="${value.replace(
            /"/g,
            '\\"'
          )}"]`
        );
        if (input && !input.checked) {
          input.checked = true;
          this.updateOptionLabel(input);
          this.triggerVariantChange();
        }
      }
    });
  }

  triggerVariantChange() {
    const variantSelector = this.querySelector("product-variants");
    this.productGallery = this.querySelector("[data-js-product-gallery]");
    this.productSlider = this.querySelector("css-slider");
    if (variantSelector) {
      variantSelector.onVariantChange();
      this.featuredImage(variantSelector.currentVariant);
    }
  }

  featuredImage(variant) {
    if (variant.featured_media != null) {
      let variantImg = this.productGallery.querySelector(
        `.product-gallery-item[data-media-id="${variant.featured_media.id}"]`
      );
      console.log(variantImg);
      if (variantImg) {
        if (this.productGallery.classList.contains("product-gallery--slider")) {
          if (
            this.productGallery.querySelector("css-slider") &&
            this.productGallery.querySelector("css-slider").sliderEnabled
          ) {
            this.productGallery
              .querySelector("css-slider")
              .changeSlide(variantImg.dataset.index);
          } else {
            this.firstProductGalleryIndex = variantImg.dataset.index;
          }
        } else {
          window.scrollTo({
            top: variantImg.getBoundingClientRect().top + window.scrollY,
            behavior: "smooth",
          });
        }
      }
    }
  }

  updateOptionLabel(input) {
    const label = input
      .closest(".product-variant")
      .querySelector(".product-variant__item-text-label");
    if (label) {
      label.textContent = input.value;
    }
  }

  startLoading() {
    this.classList.add("is-loading");
  }

  stopLoading() {
    this.classList.remove("is-loading");
  }

  cloneVariantInput() {
    const form = this.querySelector("form");
    const template = this.querySelector("template#variant_id");
    if (form && template) form.append(template.content.cloneNode(true));
  }

  clonePropertyInput() {
    const form = this.querySelector("form");
    const template = this.querySelector("template#property");
    if (form && template) {
      const clone = template.content.cloneNode(true);
      const inputElement = clone.querySelector("input"); // Assuming the element you want to modify is an input
      if (inputElement) {
        inputElement.setAttribute("name", `properties[_quick_view]`);
        inputElement.setAttribute("value", `${this.propertyType}`);
      }
      form.append(clone);
    }
  }

  openSidebar() {
    const sidebar = document.getElementById("site-quick-view-sidebar");
    if (sidebar) {
      sidebar.show();
    }
  }

  closeSidebar() {
    const sidebar = document.getElementById("site-quick-view-sidebar");
    if (sidebar) {
      sidebar.hide();
      this.mergedProductOptions.clear(); // Clear merged product options
    }
  }

  handleAddToCart() {
    this.closeSidebar();
    document.getElementById("site-cart-sidebar")?.show();
    document
      .getElementById("cart-recommendations-sidebar")
      ?.generateRecommendations();
    if (this.addToCartEnter) {
      setTimeout(() => {
        document.querySelector("#site-cart-sidebar [data-js-close]")?.focus();
      }, 200);
    }
  }

  refreshOptionValue() {
    const handler = (e) => {
      const elm = e.currentTarget;
      const label = elm
        .closest(".product-variant")
        .querySelector(".product-variant__item-text-label");
      if (label) {
        label.textContent = elm.querySelector("input").value;
      }
    };

    this.querySelectorAll(
      ".product-variant__item--radio" || ".product-variant__input"
    ).forEach((elm) => elm.addEventListener("click", handler));
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

customElements.get("quick-view") ||
  customElements.define("quick-view", QuickView);
